import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, type HistoryItem } from '../lib/api'
import { Screen, Notice } from '../components/Layout'

/**
 * Charging history — PRD v1.1 §18.
 *
 * History is a customer *billing* record, not a list of OCPP transactions. Amounts and
 * the per-interval TOU breakdown (§25) arrive with the billing pipeline; until then the
 * list deliberately shows no money rather than showing a zero, because a zero reads as
 * "free" and is the kind of thing that ends up in a dispute.
 */
export default function History() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<HistoryItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .history(20)
      .then(setRows)
      .catch(() => setError('無法載入充電紀錄，請稍後再試。'))
  }, [])

  if (error) {
    return (
      <Screen title="充電紀錄" back="/">
        <Notice tone="error">{error}</Notice>
      </Screen>
    )
  }

  if (!rows) {
    return (
      <Screen title="充電紀錄" back="/">
        <Notice tone="muted">載入中…</Notice>
      </Screen>
    )
  }

  if (rows.length === 0) {
    return (
      <Screen title="充電紀錄" back="/">
        <Notice tone="muted">尚無充電紀錄。</Notice>
      </Screen>
    )
  }

  return (
    <Screen title="充電紀錄" back="/">
      <ul className="history">
        {rows.map((r) => (
          <li
            key={r.session_id}
            className="history-item"
            onClick={() => navigate(`/charging/${r.session_id}`)}
          >
            <div className="history-when">
              {r.started_at
                ? new Date(r.started_at).toLocaleString('zh-TW', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '未開始'}
            </div>
            <div className="history-station">{r.station_name ?? r.evse_id}</div>
            <div className="history-meta">
              {r.evse_id} · 接頭 {r.connector_id}
            </div>
            <div className="history-meta">
              {r.energy_kwh ? `${r.energy_kwh} kWh` : '—'}
              {r.duration_seconds
                ? ` · ${Math.round(r.duration_seconds / 60)} 分鐘`
                : ''}
            </div>
            <div className={`history-state state-${r.state}`}>{label(r.state)}</div>
          </li>
        ))}
      </ul>
    </Screen>
  )
}

function label(state: string): string {
  return (
    {
      COMPLETED: '已完成',
      CHARGING: '充電中',
      STARTING: '啟動中',
      STOPPING: '停止中',
      FINALIZING: '結算中',
      RECONCILING: '對帳中',
      START_TIMEOUT: '未能開始',
      START_FAILED: '啟動失敗',
      AUTHORIZATION_FAILED: '授權失敗',
      PAYMENT_FAILED: '付款失敗',
      DEBT: '待付款',
    }[state] ?? state
  )
}
